import cx from 'classnames';
import Script from 'next/script';
import {
  FeaturesSection,
  FeaturesSectionItem,
} from 'src/components/features/index';
import FooterExtension from 'src/components/footer-extension';
import {
  Breadcrumb,
  PageHeaderCms as PageHeader,
} from 'src/components/headers/';
import { Link } from 'src/components/Inputs';
import { SupportingText } from 'src/components/Layout';
import { IntroSectionCms } from 'src/components/Layout/IntroSection';
import { ProductsCodeDemo } from 'src/components/Surfaces';
import { CmsThemes } from 'src/lib/constants';
import { TemplateWrapper } from 'src/templates';
import { InstitutionPageProps } from './';
import styles from './InstitutionTemplate.module.scss';

const InstitutionTemplate = ({
  metaDescription,
  metaTitle,
  pageSlug,
  pageHeaderProps,
  footerExtensionProps,
  supportingTextProps,
  featuresPlaidIntegrationProps,
  featuresInstitutionMetadataProps,
  allEndpointDemoData,
  introSectionProps,
  institutionName,
  internalLinksProps,
}: InstitutionPageProps): JSX.Element => {
  const { products: codeDemoProducts } = featuresInstitutionMetadataProps;
  return (
    <TemplateWrapper
      meta-title={metaTitle}
      meta-description={metaDescription}
      hasFooterCta={false}
      noIndex={false}
      className='institution-page'
      cmsTheme={CmsThemes.WEB3}
    >
      <Script
        id='institutions-breadcrumb-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Financial Institutions',
                item: 'https://plaid.com/institutions/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: `${institutionName}`,
                item: `https://plaid.com/institutions/${pageSlug}/`,
              },
            ],
          }),
        }}
      />
      <PageHeader
        {...pageHeaderProps}
        breadcrumb={
          <Breadcrumb>
            <Link href={'/institutions/'}>
              <a>Financial Institutions</a>
            </Link>
            <span>{institutionName}</span>
          </Breadcrumb>
        }
      />
      <FeaturesSection
        hasThreeColumns={featuresInstitutionMetadataProps.hasThreeColumns}
        classes='background background--color-black100'
      >
        {featuresInstitutionMetadataProps.accountTypes.length > 0 && (
          <FeaturesSectionItem
            hasSeparator={true}
            hasSimpleContent={true}
            hasThreeColumns={featuresInstitutionMetadataProps.hasThreeColumns}
            isSingleRow={true}
            title='Account type'
            content={
              <ul>
                {featuresInstitutionMetadataProps.accountTypes.map(
                  (accountType) => {
                    return <li>{accountType}</li>;
                  },
                )}
              </ul>
            }
          />
        )}
        <FeaturesSectionItem
          hasSeparator={true}
          hasSimpleContent={true}
          hasThreeColumns={featuresInstitutionMetadataProps.hasThreeColumns}
          isSingleRow={true}
          title='Supported Plaid products'
          content={
            <ul>
              {featuresInstitutionMetadataProps.products.map((product) => {
                return <li key={product}>{product}</li>;
              })}
            </ul>
          }
        />
        <FeaturesSectionItem
          hasSeparator={true}
          hasSimpleContent={true}
          hasThreeColumns={featuresInstitutionMetadataProps.hasThreeColumns}
          isSingleRow={true}
          title='Geography'
          content={
            <ul>
              <li>{featuresInstitutionMetadataProps.country}</li>
            </ul>
          }
        />
      </FeaturesSection>

      <IntroSectionCms {...introSectionProps} />

      <FeaturesSection
        hasThreeColumns={featuresPlaidIntegrationProps.hasThreeColumns}
      >
        {featuresPlaidIntegrationProps.items.map((item, idx) => {
          return (
            <FeaturesSectionItem
              key={`feature-item-${idx}`}
              {...item}
              hasThreeColumns={featuresPlaidIntegrationProps.hasThreeColumns}
              isSingleRow={true}
              content={<p>{item.content}</p>}
            />
          );
        })}
      </FeaturesSection>
      <ProductsCodeDemo
        data={allEndpointDemoData}
        products={codeDemoProducts}
      />
      <SupportingText {...supportingTextProps} />
      <FeaturesSection
        hasThreeColumns={true}
        label='ADDITIONAL SUPPORTED INSTITUTIONS'
        classes='background background--color-black100'
      >
        {internalLinksProps?.map((item) => {
          return (
            <div className='cell small-12 medium-4' key={item.title}>
              <h4 className={styles.internalLinkHeading}>
                <Link href={item.href}>
                  <a>{item.title}</a>
                </Link>
              </h4>
            </div>
          );
        })}
        <div className={cx('cell small-12 medium-6', styles.internalLinksCta)}>
          <FeaturesSectionItem
            isSingleRow={true}
            cta={{ href: '/institutions/', text: 'See all Institutions' }}
            title=''
            content=''
          />
        </div>
      </FeaturesSection>
      <FooterExtension {...footerExtensionProps} />
    </TemplateWrapper>
  );
};

export default InstitutionTemplate;
